import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, Loader2, SearchX, BookmarkPlus, Lock, Crown, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtPrice, fmtNum, fmtPct, changeClass, trendBadgeClass } from "../lib/format";
import { AddToWatchlist } from "../components/AddToWatchlist";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const EMPTY_FILTERS = {
  sector: "any",
  pe_min: "",
  pe_max: "",
  dividend_yield_min: "",
  market_cap_min: "",
  rsi_min: "",
  rsi_max: "",
  trend: "any",
};

export default function Scanner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isPremium = user?.tier === "premium";
  const activePreset = searchParams.get("preset") || "";
  const [presets, setPresets] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customLocked, setCustomLocked] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [wlName, setWlName] = useState("");
  const navigate = useNavigate();

  const saveAsWatchlist = async () => {
    if (!wlName.trim() || !data?.results?.length) return;
    try {
      await api.post("/watchlists", { name: wlName.trim(), tickers: data.results.map((r) => r.ticker) });
      toast.success(`Watchlist "${wlName.trim()}" created with ${data.results.length} stocks`);
      setSaveOpen(false);
      setWlName("");
      navigate("/watchlists");
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
  };

  useEffect(() => {
    api.get("/scanner/presets").then((r) => setPresets(r.data.presets));
    api.get("/market/sectors").then((r) => setSectors(r.data.sectors));
  }, []);

  const runScreen = useCallback(async (body) => {
    setLoading(true);
    try {
      const { data } = await api.post("/scanner/screen", body);
      setData(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activePreset) runScreen({ preset: activePreset });
    else if (isPremium) runScreen({});
    else setSearchParams({ preset: "undervalued_quality" }, { replace: true }); // free tier default: presets
  }, [activePreset, runScreen, isPremium, setSearchParams]);

  const applyCustom = () => {
    const body = {};
    if (filters.sector !== "any") body.sector = filters.sector;
    if (filters.trend !== "any") body.trend = filters.trend;
    for (const k of ["pe_min", "pe_max", "dividend_yield_min", "rsi_min", "rsi_max"]) {
      if (filters[k] !== "" && !isNaN(Number(filters[k]))) body[k] = Number(filters[k]);
    }
    if (filters.market_cap_min !== "" && !isNaN(Number(filters.market_cap_min)))
      body.market_cap_min = Number(filters.market_cap_min) * 1e9;
    setSearchParams({});
    runScreen(body);
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <div className="overline-label mb-2">Screen the universe</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white">Market Scanner</h1>
      </div>

      {/* Preset selector */}
      <div className="flex flex-wrap gap-2" data-testid="preset-tabs">
        {presets.map((p) => (
          <button
            key={p.id}
            data-testid={`preset-tab-${p.id}`}
            onClick={() => setSearchParams({ preset: p.id })}
            className={`px-4 py-2 text-sm border rounded-sm transition-colors duration-150 ${
              activePreset === p.id
                ? "border-primary bg-primary/10 text-white"
                : "border-border text-muted-foreground hover:text-white hover:bg-white/5"
            }`}
          >
            {p.name}
          </button>
        ))}
        <button
          data-testid="preset-tab-custom"
          onClick={() => {
            if (!isPremium) {
              setCustomLocked(true);
              return;
            }
            setSearchParams({});
            runScreen({});
          }}
          className={`px-4 py-2 text-sm border rounded-sm transition-colors duration-150 flex items-center gap-2 ${
            !activePreset && isPremium
              ? "border-primary bg-primary/10 text-white"
              : "border-border text-muted-foreground hover:text-white hover:bg-white/5"
          }`}
        >
          {isPremium ? <SlidersHorizontal className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5 text-yellow-500" />} Custom
        </button>
      </div>

      {/* Free tier: custom screening locked */}
      {customLocked && !isPremium && (
        <div className="widget p-5 border-yellow-500/40 flex items-start gap-4" data-testid="custom-locked-panel">
          <Crown className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-white tracking-tight">Custom screening is a Premium feature</div>
            <p className="text-sm text-muted-foreground mt-1 mb-3">
              Free tier includes all four preset screens. Premium unlocks fully custom filters — sector, PE,
              yield, market cap, RSI and trend — plus plain-English match reasons on every result.
            </p>
            <Link to="/upgrade" data-testid="custom-locked-upgrade-link">
              <Button className="rounded-sm bg-primary hover:bg-primary/85 text-white h-9">Upgrade to Premium</Button>
            </Link>
          </div>
          <button onClick={() => setCustomLocked(false)} data-testid="custom-locked-dismiss"
            className="text-muted-foreground/40 hover:text-white transition-colors duration-150">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Custom filters */}
      {!activePreset && isPremium && (
        <div className="widget p-5" data-testid="custom-filters-panel">
          <div className="overline-label mb-4">Custom filters</div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 items-end">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-muted-foreground">Sector</Label>
              <Select value={filters.sector} onValueChange={(v) => setFilters({ ...filters, sector: v })}>
                <SelectTrigger className="rounded-sm bg-card h-9 text-sm" data-testid="filter-sector-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  <SelectItem value="any">All sectors</SelectItem>
                  {sectors.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">PE min</Label>
              <Input type="number" className="rounded-sm h-9 font-mono text-sm bg-card" data-testid="filter-pe-min"
                value={filters.pe_min} onChange={(e) => setFilters({ ...filters, pe_min: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">PE max</Label>
              <Input type="number" className="rounded-sm h-9 font-mono text-sm bg-card" data-testid="filter-pe-max"
                value={filters.pe_max} onChange={(e) => setFilters({ ...filters, pe_max: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Yield ≥ %</Label>
              <Input type="number" className="rounded-sm h-9 font-mono text-sm bg-card" data-testid="filter-yield-min"
                value={filters.dividend_yield_min}
                onChange={(e) => setFilters({ ...filters, dividend_yield_min: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Mkt cap ≥ $B</Label>
              <Input type="number" className="rounded-sm h-9 font-mono text-sm bg-card" data-testid="filter-mcap-min"
                value={filters.market_cap_min}
                onChange={(e) => setFilters({ ...filters, market_cap_min: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">RSI min</Label>
              <Input type="number" className="rounded-sm h-9 font-mono text-sm bg-card" data-testid="filter-rsi-min"
                value={filters.rsi_min} onChange={(e) => setFilters({ ...filters, rsi_min: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">RSI max</Label>
              <Input type="number" className="rounded-sm h-9 font-mono text-sm bg-card" data-testid="filter-rsi-max"
                value={filters.rsi_max} onChange={(e) => setFilters({ ...filters, rsi_max: e.target.value })} />
            </div>
          </div>
          <div className="flex items-end gap-4 mt-4">
            <div className="space-y-1.5 w-44">
              <Label className="text-xs text-muted-foreground">Trend</Label>
              <Select value={filters.trend} onValueChange={(v) => setFilters({ ...filters, trend: v })}>
                <SelectTrigger className="rounded-sm bg-card h-9 text-sm" data-testid="filter-trend-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  <SelectItem value="any">Any trend</SelectItem>
                  <SelectItem value="uptrend">Uptrend</SelectItem>
                  <SelectItem value="downtrend">Downtrend</SelectItem>
                  <SelectItem value="sideways">Sideways</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={applyCustom} className="rounded-sm h-9 bg-primary hover:bg-primary/85 text-white"
              data-testid="apply-filters-button">
              Apply filters
            </Button>
            <Button variant="ghost" className="rounded-sm h-9 text-muted-foreground hover:text-white"
              data-testid="reset-filters-button"
              onClick={() => { setFilters(EMPTY_FILTERS); runScreen({}); }}>
              Reset
            </Button>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="widget overflow-hidden" data-testid="scanner-results">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="overline-label">{data?.screen || "Results"}</span>
          <div className="flex items-center gap-3">
            {!loading && data?.results?.length > 0 && (
              <button onClick={() => setSaveOpen(true)} data-testid="save-as-watchlist-button"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-primary/40 text-primary rounded-sm hover:bg-primary/10 transition-colors duration-150">
                <BookmarkPlus className="w-3.5 h-3.5" /> Save as watchlist
              </button>
            )}
            <span className="font-mono text-xs text-muted-foreground" data-testid="results-count">
              {loading ? "…" : `${data?.count ?? 0} matches`}
            </span>
          </div>
        </div>
        {loading ? (
          <div className="p-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Screening universe…
          </div>
        ) : data?.results?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  {["Ticker", "Name", "Sector", "Price", "PE", "Yield", "RSI", "Trend", "Match reason", ""].map((h, i) => (
                    <th key={h} className={`px-4 py-2.5 overline-label whitespace-nowrap ${i >= 3 && i <= 6 ? "text-right" : ""}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr
                    key={r.ticker}
                    data-testid={`result-row-${r.ticker}`}
                    onClick={() => navigate(`/stock/${r.ticker}`)}
                    className="border-b border-border/50 even:bg-white/[0.02] hover:bg-white/5 cursor-pointer transition-colors duration-150"
                  >
                    <td className="px-4 py-3 font-mono font-medium text-primary whitespace-nowrap">{r.ticker}</td>
                    <td className="px-4 py-3 text-gray-300 max-w-[180px] truncate">{r.name}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{r.sector}</td>
                    <td className="px-4 py-3 font-mono text-right whitespace-nowrap">
                      <div className="text-white">{fmtPrice(r.price, r.currency)}</div>
                      <div className={`text-xs ${changeClass(r.change_pct)}`}>{fmtPct(r.change_pct)}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-right text-gray-300">{r.pe != null ? fmtNum(r.pe, 1) : "—"}</td>
                    <td className="px-4 py-3 font-mono text-right text-gray-300">
                      {r.dividend_yield != null ? `${fmtNum(r.dividend_yield, 2)}%` : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-right text-gray-300">{r.rsi != null ? fmtNum(r.rsi, 0) : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[11px] font-mono uppercase border rounded-sm ${trendBadgeClass(r.trend)}`}>
                        {r.trend}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[320px]">
                      {r.match_reason ||
                        (data?.reasons_locked ? (
                          <Link to="/upgrade" onClick={(e) => e.stopPropagation()} data-testid={`reason-locked-${r.ticker}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-yellow-500/40 text-yellow-500 rounded-sm text-[10px] font-mono uppercase tracking-wider hover:bg-yellow-500/10">
                            <Lock className="w-3 h-3" /> Premium
                          </Link>
                        ) : ("—"))}
                    </td>
                    <td className="px-2 py-3">
                      <AddToWatchlist ticker={r.ticker} compact />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 flex flex-col items-center justify-center text-muted-foreground gap-3" data-testid="empty-results">
            <SearchX className="w-8 h-8 opacity-50" />
            <p className="text-sm">No stocks in the universe match these criteria right now.</p>
          </div>
        )}
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-sm bg-card border-border sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Save screen as watchlist</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Creates a watchlist with the current {data?.count ?? 0} results.
            </DialogDescription>
          </DialogHeader>
          <Input autoFocus placeholder={data?.screen || "Watchlist name"} className="rounded-sm bg-background"
            data-testid="save-watchlist-name-input" value={wlName} onChange={(e) => setWlName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveAsWatchlist()} />
          <Button onClick={saveAsWatchlist} disabled={!wlName.trim()} data-testid="save-watchlist-confirm-button"
            className="rounded-sm bg-primary hover:bg-primary/85 text-white">
            Create watchlist
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
